import axios from 'axios';
import { tokenManager } from './tokenManager';
import { sanitizeInput, sanitizeResponse } from './sanitize';
import { API_CONFIG } from '../config/api';

const API_URL = API_CONFIG.BASE_URL;

const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 180000, // 180 seconds (3 minutes) timeout - increased for better reliability (especially for login)
    headers: {
        'Content-Type': 'application/json'
    },
    // Auth disabled: do not send cookies by default
    withCredentials: false,
    // Add request timeout handling
    validateStatus: function (status) {
        return status >= 200 && status < 500; // Don't throw on 4xx errors
    }
});

// Request interceptor
axiosInstance.interceptors.request.use(
    (config) => {
        // Automatically add schoolID to requests if not already present
        // Skip for login/auth endpoints (they don't need schoolID)
        const isAuthEndpoint = config.url && (
            config.url.includes('/login') || 
            config.url.includes('/refresh') || 
            config.url.includes('/logout') ||
            config.url.includes('/auth/')
        );
        
        // Auth disabled: do not add Authorization or x-school-id headers
            delete config.headers.Authorization;
        delete config.headers['x-school-id'];
        if (!isAuthEndpoint && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
            // Keep schoolID in request body for endpoints that need it
            // if (config.data.schoolID) delete config.data.schoolID; // Commented out to allow schoolID
        }
        
        // Sanitize request data (except FormData and auth endpoints)
        // Skip sanitization for login/auth endpoints to avoid breaking credentials
        if (config.data && !(config.data instanceof FormData) && !isAuthEndpoint) {
            try {
                config.data = sanitizeInput(config.data);
            } catch (sanitizeError) {
                console.warn('Sanitization error (non-blocking):', sanitizeError);
                // Continue with original data if sanitization fails
            }
        }
        
        // Set longer timeout for file uploads
        if (config.data instanceof FormData) {
            config.timeout = API_CONFIG.UPLOAD_TIMEOUT;
        }
        
        // Ensure timeout is set (use request timeout or default)
        // For auth endpoints, respect the longer timeout if set
        if (!config.timeout) {
            config.timeout = isAuthEndpoint ? 180000 : API_CONFIG.TIMEOUT; // 180s for auth, 180s default
        } else if (isAuthEndpoint && config.timeout < 180000) {
            // If auth endpoint has a timeout but it's less than 180s, increase it
            config.timeout = Math.max(config.timeout, 180000);
        }
        
        // Log request for debugging (only in development)
        if (process.env.NODE_ENV === 'development' && isAuthEndpoint) {
            console.log('🔵 Login request config:', {
                url: config.url,
                method: config.method,
                timeout: config.timeout,
                hasData: !!config.data,
                baseURL: config.baseURL
            });
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor with token refresh
axiosInstance.interceptors.response.use(
    (response) => {
        // Sanitize response data (with error handling)
        try {
            return sanitizeResponse(response);
        } catch (sanitizeError) {
            console.warn('Response sanitization error (non-blocking):', sanitizeError);
            // Return original response if sanitization fails
            return response;
        }
    },
    async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retried, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Try to get refresh token from cookie first (if using httpOnly cookies)
                // Fallback to localStorage for backward compatibility
                let refreshToken = null;
                try {
                    // Check if cookie is available (will be null if httpOnly)
                    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
                        const [key, value] = cookie.trim().split('=');
                        acc[key] = value;
                        return acc;
                    }, {});
                    refreshToken = cookies.refreshToken;
                } catch (e) {
                    // Cookie not accessible (httpOnly), use localStorage
                }
                
                if (!refreshToken) {
                    refreshToken = tokenManager.getRefreshToken();
                }
                
                if (!refreshToken) {
                    throw new Error('No refresh token');
                }

                // Call refresh endpoint - send token in body for backward compatibility
                // Server will also check cookies
                const response = await axios.post(
                    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH}`,
                    { refreshToken },
                    { withCredentials: true } // Include cookies
                );

                const { accessToken, refreshToken: newRefreshToken } = response.data;
                // Store both tokens - use new refresh token if provided, otherwise keep existing
                tokenManager.setTokens(accessToken, newRefreshToken || refreshToken);

                // Retry original request with new token
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return axiosInstance(originalRequest);

            } catch (refreshError) {
                // Refresh failed - logout user
                tokenManager.clearTokens();
                if (typeof window !== 'undefined') {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        // Handle other error cases
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    // Unauthorized - clear token and redirect to login
                    tokenManager.clearTokens();
                    if (typeof window !== 'undefined') {
                        window.location.href = '/login';
                    }
                    break;
                case 403:
                    // Forbidden - user doesn't have permission
                    console.error('Access forbidden');
                    break;
                case 404:
                    // Not found
                    console.error('Resource not found');
                    break;
                case 500:
                    // Server error
                    console.error('Server error');
                    break;
                default:
                    console.error('An error occurred');
            }
        } else if (error.request) {
            // Network error - server not running
            console.error('Network error - server may not be running');
            // Don't redirect to login on network errors, just show error
        } else if (error.code === 'ECONNABORTED') {
            // Timeout error
            console.error('Request timeout - the request took too long to complete');
        } else {
            // Other error
            console.error('Error:', error.message);
        }
        return Promise.reject(error);
    }
);

export default axiosInstance; 